import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as MaintenanceDashboardActions from './maintenance-dashboard.actions';
import { MaintenanceDashboardService } from '../../../../core/services/gse/maintenance-dashboard.service';
import { ToastActions } from '../../../toast/toast.actions';
import { forkJoin } from 'rxjs';
import { TechnicianService } from '../../../../core/services/gse/technician.service';

@Injectable()
export class MaintenanceDashboardEffects {
  private actions$ = inject(Actions);
  private maintenanceDashboardService = inject(MaintenanceDashboardService);
  private technicianService = inject(TechnicianService);

  loadPlannerSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadPlannerSummary),
      mergeMap(() =>
        this.maintenanceDashboardService.getPlannerSummaryMerged().pipe(
          map((summary) =>
            MaintenanceDashboardActions.loadPlannerSummarySuccess({ summary })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadPlannerSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load planner dashboard summary',
              })
            )
          )
        )
      )
    )
  );

  loadSupervisorSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadSupervisorSummary),
      mergeMap(() =>
        this.maintenanceDashboardService.getSupervisorSummary().pipe(
          map((summary) =>
            MaintenanceDashboardActions.loadSupervisorSummarySuccess({ summary })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadSupervisorSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load supervisor dashboard summary',
              })
            )
          )
        )
      )
    )
  );

  loadMaintenanceOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadMaintenanceOverview),
      mergeMap(() =>
        this.maintenanceDashboardService.getMaintenanceOverviewMerged().pipe(
          map((summary) =>
            MaintenanceDashboardActions.loadMaintenanceOverviewSuccess({ summary })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadMaintenanceOverviewFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load maintenance overview',
              })
            )
          )
        )
      )
    )
  );

  loadSupervisorJobCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadSupervisorJobCards),
      mergeMap(({ from, to }) =>
        this.technicianService.getAllJobCards({ page: 0, size: 10, sort: 'createdAt,desc', from, to }).pipe(
          map((response) =>
            MaintenanceDashboardActions.loadSupervisorJobCardsSuccess({
              items: response.data ?? [],
            })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadSupervisorJobCardsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load supervisor job cards',
              })
            )
          )
        )
      )
    )
  );

  loadDailyCheckins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadDailyCheckins),
      mergeMap(({ from, to }) =>
        this.maintenanceDashboardService.getDailyCheckins({ page: 0, size: 10, sort: 'createdAt,desc', from, to }).pipe(
          map((items) =>
            MaintenanceDashboardActions.loadDailyCheckinsSuccess({ items })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadDailyCheckinsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load daily checkins',
              })
            )
          )
        )
      )
    )
  );

  loadPlannerSummaryFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MaintenanceDashboardActions.loadPlannerSummaryFailure,
          MaintenanceDashboardActions.loadSupervisorSummaryFailure,
          MaintenanceDashboardActions.loadSupervisorJobCardsFailure,
          MaintenanceDashboardActions.loadDailyCheckinsFailure,
          MaintenanceDashboardActions.loadMaintenanceAlertsFailure,
          MaintenanceDashboardActions.loadMaintenanceWeeklyOverviewFailure,
          MaintenanceDashboardActions.loadBackJobsSummaryFailure,
          MaintenanceDashboardActions.loadMaintenanceOverviewFailure,
          MaintenanceDashboardActions.loadInspectorSummaryFailure,
          MaintenanceDashboardActions.loadOverdueJobsSummaryFailure,
          MaintenanceDashboardActions.loadInspectorJobCardsFailure,
          MaintenanceDashboardActions.loadOverdueJobCardsFailure,
          MaintenanceDashboardActions.loadTechnicianEfficiencyFailure,
          MaintenanceDashboardActions.loadWaitingForSparesFailure
        ),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadMaintenanceAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadMaintenanceAlerts),
      mergeMap(({ from, to, page, size, alertType }) => {
        const currentPage = page ?? 0;
        const currentSize = size ?? 10;
        const currentType = alertType ?? 'BOTH';

        if (currentType === 'CRITICAL') {
          return this.maintenanceDashboardService
            .getMaintenanceAlerts({
              from,
              to,
              page: currentPage,
              size: currentSize,
              type: 'CRITICAL',
            })
            .pipe(
              map((critical) =>
                MaintenanceDashboardActions.loadMaintenanceAlertsSuccess({
                  critical: critical.data,
                  upcoming: [],
                  page: currentPage,
                  size: currentSize,
                  criticalCount: critical.data?.length ?? 0,
                  upcomingCount: 0,
                  alertType: currentType,
                })
              ),
              catchError((error) =>
                of(
                  MaintenanceDashboardActions.loadMaintenanceAlertsFailure({
                    error:
                      error?.error?.message ||
                      error?.message ||
                      'Failed to load maintenance alerts',
                  })
                )
              )
            );
        }

        if (currentType === 'UPCOMING') {
          return this.maintenanceDashboardService
            .getMaintenanceAlerts({
              from,
              to,
              page: currentPage,
              size: currentSize,
              type: 'UPCOMING',
            })
            .pipe(
              map((upcoming) =>
                MaintenanceDashboardActions.loadMaintenanceAlertsSuccess({
                  critical: [],
                  upcoming: upcoming.data,
                  page: currentPage,
                  size: currentSize,
                  criticalCount: 0,
                  upcomingCount: upcoming.data?.length ?? 0,
                  alertType: currentType,
                })
              ),
              catchError((error) =>
                of(
                  MaintenanceDashboardActions.loadMaintenanceAlertsFailure({
                    error:
                      error?.error?.message ||
                      error?.message ||
                      'Failed to load maintenance alerts',
                  })
                )
              )
            );
        }

        return forkJoin({
          critical: this.maintenanceDashboardService.getMaintenanceAlerts({
            from,
            to,
            page: currentPage,
            size: currentSize,
            type: 'CRITICAL',
          }),
          upcoming: this.maintenanceDashboardService.getMaintenanceAlerts({
            from,
            to,
            page: currentPage,
            size: currentSize,
            type: 'UPCOMING',
          }),
        }).pipe(
          map(({ critical, upcoming }) =>
            MaintenanceDashboardActions.loadMaintenanceAlertsSuccess({
              critical: critical.data,
              upcoming: upcoming.data,
              page: currentPage,
              size: currentSize,
              criticalCount: critical.data?.length ?? 0,
              upcomingCount: upcoming.data?.length ?? 0,
              alertType: currentType,
            })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadMaintenanceAlertsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load maintenance alerts',
              })
            )
          )
        );
      })

    )
  );

  loadMaintenanceWeeklyOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadMaintenanceWeeklyOverview),
      mergeMap(({ from, to }) =>
        this.maintenanceDashboardService.getMaintenanceWeeklyOverview({ from, to }).pipe(
          map((data) =>
            MaintenanceDashboardActions.loadMaintenanceWeeklyOverviewSuccess({ data })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadMaintenanceWeeklyOverviewFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load maintenance weekly overview',
              })
            )
          )
        )
      )
    )
  );

  loadBackJobsSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadBackJobsSummary),
      mergeMap(() =>
        this.maintenanceDashboardService.getBackJobsSummaryMerged().pipe(
          map((summary) => MaintenanceDashboardActions.loadBackJobsSummarySuccess({ summary })),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadBackJobsSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load back jobs summary',
              })
            )
          )
        )
      )
    )
  );

  loadInspectorSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadInspectorSummary),
      mergeMap(() =>
        this.maintenanceDashboardService.getInspectorSummaryMerged().pipe(
          map((summary) => MaintenanceDashboardActions.loadInspectorSummarySuccess({ summary })),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadInspectorSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load inspector dashboard summary',
              })
            )
          )
        )
      )
    )
  );

  loadOverdueJobsSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadOverdueJobsSummary),
      mergeMap(() =>
        this.maintenanceDashboardService.getOverdueJobsSummaryMerged().pipe(
          map((summary) => MaintenanceDashboardActions.loadOverdueJobsSummarySuccess({ summary })),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadOverdueJobsSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load overdue jobs summary',
              })
            )
          )
        )
      )
    )
  );

  loadInspectorJobCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadInspectorJobCards),
      mergeMap(({ params }) =>
        this.technicianService.getAllJobCards(params).pipe(
          map((response) =>
            MaintenanceDashboardActions.loadInspectorJobCardsSuccess({
              items: response.data ?? [],
            })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadInspectorJobCardsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load job cards',
              })
            )
          )
        )
      )
    )
  );

  loadOverdueJobCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadOverdueJobCards),
      mergeMap(({ params }) =>
        this.maintenanceDashboardService.getAllOverDueJobCards(params).pipe(
          map((response) =>
            MaintenanceDashboardActions.loadOverdueJobCardsSuccess({
              items: response.data ?? [],
              page: response.page,
              totalPages: response.totalPages,
            })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadOverdueJobCardsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load overdue jobs',
              })
            )
          )
        )
      )
    )
  );

  loadTechnicianEfficiency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadTechnicianEfficiency),
      mergeMap(({ limit, from, to }) =>
        this.maintenanceDashboardService.getTechnicianEfficiency({ limit, from, to }).pipe(
          map((data) =>
            MaintenanceDashboardActions.loadTechnicianEfficiencySuccess({ data })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadTechnicianEfficiencyFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load technician efficiency',
              })
            )
          )
        )
      )
    )
  );

  loadWaitingForSpares$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MaintenanceDashboardActions.loadWaitingForSpares),
      mergeMap(({ page = 0, size = 10, sort = 'createdAt,desc' }) =>
        this.maintenanceDashboardService.getWaitingForSpares({ page, size, sort }).pipe(
          map(({ data, total }) =>
            MaintenanceDashboardActions.loadWaitingForSparesSuccess({
              items: data,
              total: total ?? 0,
              page,
            })
          ),
          catchError((error) =>
            of(
              MaintenanceDashboardActions.loadWaitingForSparesFailure({
                error: error?.error?.message || error?.message || 'Failed to load waiting for spares',
              })
            )
          )
        )
      )
    )
  );
}
